import {
  HubGenerationInputSchema,
  HubPageSpecSchema,
  type HubGenerationInput,
  type HubPageSpec,
  type HubSection,
} from './schema'

export interface HubValidationIssue {
  path: string
  message: string
}

export interface HubValidationResult<T> {
  success: boolean
  data?: T
  issues: HubValidationIssue[]
}

export interface HubPageValidationOptions {
  expectedLayoutMode?: HubPageSpec['layoutMode']
  expectedSlug?: string
}

export function validateHubGenerationInput(
  input: unknown
): HubValidationResult<HubGenerationInput> {
  const parsed = HubGenerationInputSchema.safeParse(input)

  if (!parsed.success) {
    return {
      success: false,
      issues: parsed.error.issues.map((issue) => ({
        path: issue.path.join('.') || 'input',
        message: issue.message,
      })),
    }
  }

  const issues = validateGenerationReferences(parsed.data)

  return {
    success: issues.length === 0,
    data: parsed.data,
    issues,
  }
}

export function validateHubPageSpec(
  spec: unknown,
  input?: HubGenerationInput,
  options?: HubPageValidationOptions
): HubValidationResult<HubPageSpec> {
  const parsed = HubPageSpecSchema.safeParse(spec)

  if (!parsed.success) {
    return {
      success: false,
      issues: parsed.error.issues.map((issue) => ({
        path: issue.path.join('.') || 'spec',
        message: issue.message,
      })),
    }
  }

  const issues = validatePageSpecReferences(parsed.data, input, options)

  return {
    success: issues.length === 0,
    data: parsed.data,
    issues,
  }
}

export function validateHubPageSpecAgainstInput(
  spec: unknown,
  input: unknown,
  options?: HubPageValidationOptions
): HubValidationResult<HubPageSpec> {
  const inputResult = validateHubGenerationInput(input)

  if (!inputResult.success || !inputResult.data) {
    return {
      success: false,
      issues: inputResult.issues.map((issue) => ({
        path: `input.${issue.path}`,
        message: issue.message,
      })),
    }
  }

  return validateHubPageSpec(spec, inputResult.data, options)
}

function validateGenerationReferences(
  input: HubGenerationInput
): HubValidationIssue[] {
  const issues: HubValidationIssue[] = []
  const reportIds = new Set(input.reports.map((report) => report.reportId))
  const productIds = new Set(input.products.map((product) => product.productId))

  input.assignments.forEach((assignment, index) => {
    if (!reportIds.has(assignment.reportId)) {
      issues.push({
        path: `assignments.${index}.reportId`,
        message: `Unknown report id "${assignment.reportId}"`,
      })
    }

    const clusterSlugField =
      assignment.hubKind === 'informational'
        ? 'informationalHubSlug'
        : 'transactionalHubSlug'
    if (!input.cluster[clusterSlugField]) {
      issues.push({
        path: `cluster.${clusterSlugField}`,
        message: `Missing ${assignment.hubKind} hub slug for assignment "${assignment.assignmentId}"`,
      })
    }

    // Intentionally no hubSlug-equality check. The assignment's hubSlug is
    // denormalized metadata that can drift when planning slugs change after
    // the assignment row was created. The source of truth is the cluster's
    // resolved (informationalHubSlug | transactionalHubSlug); the build
    // step in research-hubs/service.ts normalizes assignments to match.
  })

  input.reportCandidates.forEach((candidate, candidateIndex) => {
    if (!reportIds.has(candidate.reportId)) {
      issues.push({
        path: `reportCandidates.${candidateIndex}.reportId`,
        message: `Unknown report id "${candidate.reportId}"`,
      })
    }

    candidate.topProducts.forEach((product, productIndex) => {
      if (!productIds.has(product.productId)) {
        issues.push({
          path: `reportCandidates.${candidateIndex}.topProducts.${productIndex}.productId`,
          message: `Unknown product id "${product.productId}"`,
        })
      }
    })

    candidate.painPoints.forEach((painPoint, painPointIndex) => {
      if (painPoint.productId && !productIds.has(painPoint.productId)) {
        issues.push({
          path: `reportCandidates.${candidateIndex}.painPoints.${painPointIndex}.productId`,
          message: `Unknown product id "${painPoint.productId}"`,
        })
      }
    })

    if (candidate.quickAnswer?.productId) {
      if (!productIds.has(candidate.quickAnswer.productId)) {
        issues.push({
          path: `reportCandidates.${candidateIndex}.quickAnswer.productId`,
          message: `Unknown product id "${candidate.quickAnswer.productId}"`,
        })
      }
    }
  })

  return issues
}

const INLINE_LINK_REGEX = /\[([^\[\]]+)\]\(([^()\s]+)\)/g

function collectInlineHrefs(text: string | undefined): string[] {
  if (!text) return []
  const hrefs: string[] = []
  for (const match of text.matchAll(INLINE_LINK_REGEX)) {
    const href = match[2]
    if (href) hrefs.push(href)
  }
  return hrefs
}

function validateInlineHrefs(
  section: HubSection,
  index: number,
  issues: HubValidationIssue[]
): void {
  const checks: Array<{ field: string; text: string | undefined }> = []
  switch (section.type) {
    case 'intro':
      checks.push({ field: 'body', text: section.body })
      break
    case 'guideSteps':
      section.steps.forEach((step, stepIndex) => {
        checks.push({
          field: `steps.${stepIndex}.description`,
          text: step.description,
        })
      })
      break
    case 'guidePathways':
      section.pathways.forEach((pathway, pathwayIndex) => {
        checks.push({
          field: `pathways.${pathwayIndex}.body`,
          text: pathway.body,
        })
      })
      break
    case 'faq':
      section.items.forEach((item, itemIndex) => {
        checks.push({
          field: `items.${itemIndex}.answer`,
          text: item.answer,
        })
      })
      break
    case 'topPicks':
      section.picks.forEach((pick, pickIndex) => {
        checks.push({
          field: `picks.${pickIndex}.reason`,
          text: pick.reason,
        })
      })
      break
    case 'decisionBlock':
      checks.push({ field: 'supportText', text: section.supportText })
      break
    default:
      return
  }

  for (const check of checks) {
    for (const href of collectInlineHrefs(check.text)) {
      if (!href.startsWith('/') || href.startsWith('//')) {
        issues.push({
          path: `sections.${index}.${check.field}`,
          message: `Inline link href "${href}" must be an internal absolute path starting with "/"`,
        })
      }
    }
  }
}

function validatePageSpecReferences(
  spec: HubPageSpec,
  input?: HubGenerationInput,
  options?: HubPageValidationOptions
): HubValidationIssue[] {
  const issues: HubValidationIssue[] = []
  const sectionIds = new Set<string>()

  spec.sections.forEach((section, index) => {
    if (sectionIds.has(section.id)) {
      issues.push({
        path: `sections.${index}.id`,
        message: `Duplicate section id "${section.id}"`,
      })
    }

    sectionIds.add(section.id)
    validateInlineHrefs(section, index, issues)
  })

  if (!spec.sections.some((section) => section.type === 'hero')) {
    issues.push({
      path: 'sections',
      message: 'Hub page specs must include a hero section',
    })
  }

  if (!input) {
    return issues
  }

  const reportIds = new Set(input.reports.map((report) => report.reportId))
  const reportHrefs = new Set(input.reports.map((report) => report.href))
  const productIds = new Set(input.products.map((product) => product.productId))

  if (spec.hubKind !== input.hubKind) {
    issues.push({
      path: 'hubKind',
      message: `Expected hub kind "${input.hubKind}"`,
    })
  }

  const expectedSlug =
    options?.expectedSlug ??
    (input.hubKind === 'informational'
      ? input.cluster.informationalHubSlug
      : input.cluster.transactionalHubSlug)

  if (spec.slug !== expectedSlug) {
    issues.push({
      path: 'slug',
      message: `Expected slug "${expectedSlug}"`,
    })
  }

  if (
    options?.expectedLayoutMode &&
    spec.layoutMode !== options.expectedLayoutMode
  ) {
    issues.push({
      path: 'layoutMode',
      message: `Expected layout mode "${options.expectedLayoutMode}"`,
    })
  } else if (!input.layoutModes.includes(spec.layoutMode)) {
    issues.push({
      path: 'layoutMode',
      message: `Unexpected layout mode "${spec.layoutMode}"`,
    })
  }

  spec.sections.forEach((section, index) => {
    if (section.type === 'comparisonTable') {
      section.rows.forEach((row, rowIndex) => {
        if (row.values.length !== section.columns.length) {
          issues.push({
            path: `sections.${index}.rows.${rowIndex}.values`,
            message: `Expected ${section.columns.length} values to match comparison columns; received ${row.values.length}.`,
          })
        }
      })
    }

    if (section.type === 'anchorLinkList') {
      validateAnchorLinkListReportLinks({
        section,
        sectionIndex: index,
        hubKind: input.hubKind,
        reportHrefs,
        issues,
      })
    }

    collectSectionReportIds(section).forEach((reportId) => {
      if (!reportIds.has(reportId)) {
        issues.push({
          path: `sections.${index}`,
          message: `Unknown report id "${reportId}"`,
        })
      }
    })

    collectSectionProductIds(section).forEach((productId) => {
      if (!productIds.has(productId)) {
        issues.push({
          path: `sections.${index}`,
          message: `Unknown product id "${productId}"`,
        })
      }
    })
  })

  return issues
}

function normalizeReportHref(href: string): string {
  const path = href.split(/[?#]/)[0] ?? href
  return path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path
}

function validateAnchorLinkListReportLinks(input: {
  section: Extract<HubSection, { type: 'anchorLinkList' }>
  sectionIndex: number
  hubKind: HubGenerationInput['hubKind']
  reportHrefs: Set<string>
  issues: HubValidationIssue[]
}) {
  const seenHrefs = new Set<string>()

  input.section.links.forEach((link, linkIndex) => {
    const normalizedHref = normalizeReportHref(link.href)
    const path = `sections.${input.sectionIndex}.links.${linkIndex}.href`

    if (normalizedHref !== link.href) {
      input.issues.push({
        path,
        message:
          'Anchor link list cards must use raw report links without hash anchors, query strings, or trailing slashes.',
      })
    }

    if (!input.reportHrefs.has(normalizedHref)) {
      input.issues.push({
        path,
        message: `Expected a link to one of the assigned reports; received "${link.href}".`,
      })
    }

    if (seenHrefs.has(normalizedHref)) {
      input.issues.push({
        path,
        message: `Expected only one anchor link list card for report "${normalizedHref}".`,
      })
    }

    seenHrefs.add(normalizedHref)
  })

  const missingHrefs =
    input.hubKind === 'informational'
      ? [...input.reportHrefs].filter((href) => !seenHrefs.has(href))
      : []
  if (missingHrefs.length > 0) {
    input.issues.push({
      path: `sections.${input.sectionIndex}.links`,
      message: `Expected one anchor link list card per assigned report; missing ${missingHrefs.join(', ')}.`,
    })
  }
}

function collectSectionReportIds(section: HubSection): string[] {
  const ids = [...(section.sourceReportIds ?? [])]

  switch (section.type) {
    case 'topPicks':
      section.picks.forEach((pick) => {
        if (pick.sourceReportId) ids.push(pick.sourceReportId)
      })
      break
    case 'reportGrid':
      section.items.forEach((item) => ids.push(item.reportId))
      break
    case 'guidePathways':
      section.pathways.forEach((pathway) => ids.push(pathway.reportId))
      break
    // pullQuote and methodologyCard carry a single optional sourceReportId.
    // Both were missing here, so a fabricated id on either section reached the
    // rendered page unchallenged: the citation link silently disappears at
    // render time because the reportsById lookup misses.
    case 'pullQuote':
    case 'methodologyCard':
      if (section.sourceReportId) ids.push(section.sourceReportId)
      break
    default:
      break
  }

  return ids
}

function collectSectionProductIds(section: HubSection): string[] {
  const ids = [...(section.sourceProductIds ?? [])]

  switch (section.type) {
    case 'topPicks':
      section.picks.forEach((pick) => ids.push(pick.productId))
      break
    case 'productRail':
      section.productIds.forEach((productId) => ids.push(productId))
      break
    default:
      break
  }

  return ids
}
